import React, { useState, useContext, useEffect } from 'react';
import { Grid, GridColumn, Confirm, Modal } from 'semantic-ui-react';
import BlockDetailsPanel from './blockDetails';
import BlocksList from './blocksList';
import { GroupReservationContext, actions } from '../context';
import _ from 'lodash'
import { UpsertGroupBlock, SaveReservation, UpdateReservationStatus } from '../../../api/services/pms'
import { onWarning, onError, onSuccess } from '../../../components/shared/messages';
import { ReservationStatusesEnums } from '../../../components/shared/statustypes';
import PropTypes from "prop-types";
import { useLoader } from '../../../components/shared/utils';
import { useTextResources ,useMessageResources} from '../../../language';


const BlockTab = ({ setGroupDisabled }) => {
  const loader = useLoader()
  const { state, dispatch } = useContext(GroupReservationContext)
  const reservationGroup = state.ReservationGroup
  const reservations = reservationGroup.ReservationGroupReservations
  const blocks = _.filter(reservations, "IsBlock")
  const hotelID = reservationGroup.HotelID
  const [blockActive, setBlockActive] = useState(_.first(_.filter(blocks, "IsPrimaryReservationInGroup")));
  const [blockActiveDirty, setBlockActiveDirty] = useState(false)
  const [reservationActiveState, setReservationActiveState] = useState({ ...(blockActive?.Reservation || {}) })
  const [confirmStatus, setConfirmStatus] = useState(false)
  const text = useTextResources()
  const messages = useMessageResources()
  useEffect(() => {
    dispatch({ type: actions.registerTabSave, index: 2, callback: () => { return true } });
    dispatch({ type: actions.setDirty, payload: false });
  }, [])


  const onBlockSelected = (block) => {
    setBlockActive(block)
  }

  useEffect(() => {
    setReservationActiveState({ ...blockActive.Reservation })
  }, [blockActive, blockActive.Reservation, blockActive.Reservation.Quantity, blockActive.Reservation.StatusId])

  const onSave = async (reservation) => {
    loader(true);
    //Check new block
    if (!blockActive.Reservation.Id && !blockActive.IsPrimaryReservationInGroup) {
      reservations.push(blockActive);
    }
    try {
      const data = await UpsertGroupBlock({
        ...reservationGroup, ReservationGroupReservations: [{
          ...blockActive,
          Reservation: reservation
        }]
      });
      let updatedBlock = _.first(data.ReservationGroupReservations)
      let roomType = _.find(state.RoomTypes, ['Id', updatedBlock.Reservation.RoomTypeId])
      updatedBlock.Reservation.RoomType = roomType
      updatedBlock.Reservation.RoomTypeName = roomType.Name
      updatedBlock.Reservation.Guest = reservation.Guest

      _.assign(blockActive, { ...updatedBlock })
      dispatch({ type: actions.update, payload: false });
      onSuccess();
    } catch (error) {
      _.remove(reservations, r => { return !r.Reservation.Id })
      error.status === 400 ? onWarning(error) : onError(error)
    } finally {
      loader(false);
    }
  }

  const onNewBlockClicked = () => {
    const mainBlock = _.first(_.filter(blocks, "IsPrimaryReservationInGroup"));
    let newBlock = {
      ...mainBlock,
      IsBlock: true,
      IsPrimaryReservationInGroup: false,
      Id: 0,
      ReservationID: 0,
      Reservation: {
        ...mainBlock.Reservation,
        Id: 0,
        Number: null,
        ProductID: null,
        RoomTypeId: null,
        RoomType: null,
        CreateFolio: false,
        AveragePricePerNightBeforeTax: 0,
        AveragePricePerNightAfterTax: 0,
        TotalAmountProductCurrentBeforeTax: 0,
        TotalAmountProductCurrentAfterTax: 0,
        Quantity: 1
      }
    }
    setBlockActive(newBlock)
  }

  const cancelBlock = async () => {
    setConfirmStatus(false)
    loader(true);
    let reservation = blockActive.Reservation;
    if (!reservation.Id) return
    let childReservations = _.filter(reservations, r => { return r.ParentReservationID === reservation.Id && r.Reservation.Id })
    let promises = [UpdateReservationStatus({ ...reservation, StatusId: ReservationStatusesEnums.Cancelled })]
    if (childReservations.length) {
      childReservations.forEach(r => {
        if (r.Reservation.Id) {
          let res = { ...r.Reservation, StatusId: ReservationStatusesEnums.Cancelled }
          promises.push(UpdateReservationStatus(res))
        }
      });
    }
    try {
      const results = await Promise.all(promises)
      if (results) {
        let blockReservationCancelled = _.find(results, r => { return r.Id === reservation.Id && r.StatusId === ReservationStatusesEnums.Cancelled })
        if (blockReservationCancelled) {
          _.remove(reservations, r => { return r.ReservationID === reservation.Id || r.ParentReservationID === reservation.Id })
          if (reservations.length > 0) {
            onBlockSelected(_.first(_.filter(blocks, "IsPrimaryReservationInGroup")))
          }
          else {
            setGroupDisabled()
          }
          dispatch({ type: actions.update, payload: false });
          onSuccess("BlockCancelledSuccessfully");
          loader(false);
        }
      }
    } catch (error) {
      error.status === 400 ? onWarning(error) : onError(error)
      loader(false);
    }
  }

  const onBlockCanceled = async () => {
    setConfirmStatus(true)
  }

  const onBlockWashed = async () => {
    loader(true);
    let reservation = blockActive.Reservation;
    try {
      const data = await SaveReservation({ ...reservation, Quantity: 1 });
      _.assign(reservation, { ...data, IsBlockReservaton: true })
      dispatch({ type: actions.update, payload: false });
      onSuccess("BlockWashedUpSuccessfully");
      loader(false);
    } catch (error) {
      error.status === 400 ? onWarning(error) : onError(error)
      loader(false);
    }
  }

  const onCancelChanges = () => {
    let reservation = blockActive.Reservation
    if (!reservation.Id) {
      if (blockActive.IsPrimaryReservationInGroup) {
        setReservationActiveState({ ...blockActive.Reservation })
      }
      else {
        setBlockActive(_.first(_.filter(blocks, "IsPrimaryReservationInGroup")))
      }
    }
    else {
      setReservationActiveState({ ...blockActive.Reservation })
    }
  }

  const isBlockDirtyChange = (isDirty) => {
    setBlockActiveDirty(isDirty)
  }

  const getCancelConfirmContent = () => {
    if (blockActive.IsPrimaryReservationInGroup) {
      return <Modal.Content>{messages('CancelPrimaryBlockReservationMessage')}</Modal.Content>
    }
    else {
      return <Modal.Content>{messages('CancelBlockReservationMessage')}</Modal.Content>
    }
  }

  return (
    <React.Fragment>
      <Grid columns={2} className="blocks">
        <Grid.Row>
          <Grid.Column width={6}>
            <BlockDetailsPanel
              reservation={reservationActiveState}
              reservationSavedState={{
                initRoomTypeId: blockActive.Reservation.RoomTypeId,
                initRoomsNumber: blockActive.Reservation.Quantity
              }}
              HotelID={hotelID}
              onSave={onSave}
              onCancel={onCancelChanges}
              isDirtyChange={isBlockDirtyChange}
            />
          </Grid.Column>
          <GridColumn width={10}>
            <BlocksList
              blockActive={blockActive}
              onBlockSelected={onBlockSelected}
              onNewBlockClicked={onNewBlockClicked}
              onBlockCanceled={onBlockCanceled}
              reservations={reservations}
              isDirty={blockActiveDirty}
              onBlockWashed={onBlockWashed}
            />
          </GridColumn>
        </Grid.Row>
      </Grid>
      <Confirm
        //dimmer="inverted"
        open={confirmStatus}
        header={text("CancelBlock")}
        content={getCancelConfirmContent}
        confirmButton={{ content: text("Yes"), positive: true }}
        cancelButton={{ content: text("No"), negative: true }}
        onCancel={() => setConfirmStatus(false)}
        onConfirm={cancelBlock}
        size={"tiny"}
      />
    </React.Fragment>
  )
}
BlockTab.propTypes = {
  setGroupDisabled: PropTypes.func
}
export default BlockTab